import React from 'react';
import { Helmet } from 'react-helmet';

const ConversionCalculators = () => {
  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 to-purple-100">
      <Helmet>
        <title>Conversion Calculators | Comprehensive Unit Conversion Tools</title>
        <meta name="description" content="Collection of 85 unit conversion calculators for various measurement systems. Convert length, weight, volume, temperature, and more with our accurate and user-friendly tools." />
        <meta name="keywords" content="conversion calculators, unit converter, length converter, weight converter, volume converter, temperature converter, currency converter, measurement conversion tools" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">🔄</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Conversion Calculators</h1>
          <p className="calculator-count text-xl text-gray-600">85 calculators</p>
        </div>

        <div className="max-w-2xl mx-auto mb-12">
          <div className="calculator-description bg-white p-6 rounded-lg shadow-md">
            <p className="text-gray-600 mb-4">
              Accurate unit conversions are essential in everyday life and specialized fields. Our comprehensive collection of conversion calculators makes it easy to convert between different measurement systems quickly and accurately. Whether you're converting length, weight, volume, temperature, or something more specialized, we've got you covered.
            </p>
            <button className="read-more inline-block bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors">
              Read more
            </button>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Length & Distance Conversions <span className="emoji ml-2">📏</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/meters-to-feet" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Meters to Feet Converter</h3>
                <p className="text-gray-600">Convert meters to feet and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/kilometers-to-miles" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Kilometers to Miles Converter</h3>
                <p className="text-gray-600">Convert kilometers to miles and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/cm-to-inches" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Centimeters to Inches Converter</h3>
                <p className="text-gray-600">Convert centimeters to inches and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/miles-to-kilometers" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Miles to Kilometers Converter</h3>
                <p className="text-gray-600">Convert miles to kilometers and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/yards-to-meters" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Yards to Meters Converter</h3>
                <p className="text-gray-600">Convert yards to meters and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/feet-to-meters" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Feet to Meters Converter</h3>
                <p className="text-gray-600">Convert feet to meters and vice versa</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Weight & Mass Conversions <span className="emoji ml-2">⚖️</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/kg-to-lbs" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Kilograms to Pounds Converter</h3>
                <p className="text-gray-600">Convert kilograms to pounds and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/grams-to-ounces" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Grams to Ounces Converter</h3>
                <p className="text-gray-600">Convert grams to ounces and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/tons-to-kg" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Tons to Kilograms Converter</h3>
                <p className="text-gray-600">Convert metric tons to kilograms and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/stone-to-kg" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Stone to Kilograms Converter</h3>
                <p className="text-gray-600">Convert stone to kilograms and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/oz-to-grams" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Ounces to Grams Converter</h3>
                <p className="text-gray-600">Convert ounces to grams and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/grams-to-kg" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Grams to Kilograms Converter</h3>
                <p className="text-gray-600">Convert grams to kilograms and vice versa</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Volume & Capacity Conversions <span className="emoji ml-2">💧</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/liters-to-gallons" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Liters to Gallons Converter</h3>
                <p className="text-gray-600">Convert liters to gallons and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/ml-to-oz" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Milliliters to Ounces Converter</h3>
                <p className="text-gray-600">Convert milliliters to fluid ounces and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/cups-to-ml" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Cups to Milliliters Converter</h3>
                <p className="text-gray-600">Convert cups to milliliters and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/pints-to-liters" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Pints to Liters Converter</h3>
                <p className="text-gray-600">Convert pints to liters and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/quarts-to-liters" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Quarts to Liters Converter</h3>
                <p className="text-gray-600">Convert quarts to liters and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/gallons-to-liters" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Gallons to Liters Converter</h3>
                <p className="text-gray-600">Convert gallons to liters and vice versa</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Temperature Conversions <span className="emoji ml-2">🌡️</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/celsius-to-fahrenheit" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Celsius to Fahrenheit Converter</h3>
                <p className="text-gray-600">Convert Celsius to Fahrenheit and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/celsius-to-kelvin" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Celsius to Kelvin Converter</h3>
                <p className="text-gray-600">Convert Celsius to Kelvin and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/fahrenheit-to-celsius" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Fahrenheit to Celsius Converter</h3>
                <p className="text-gray-600">Convert Fahrenheit to Celsius and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/fahrenheit-to-kelvin" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Fahrenheit to Kelvin Converter</h3>
                <p className="text-gray-600">Convert Fahrenheit to Kelvin and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/kelvin-to-celsius" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Kelvin to Celsius Converter</h3>
                <p className="text-gray-600">Convert Kelvin to Celsius and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/kelvin-to-fahrenheit" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Kelvin to Fahrenheit Converter</h3>
                <p className="text-gray-600">Convert Kelvin to Fahrenheit and vice versa</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Area Conversions <span className="emoji ml-2">📐</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/square-meters-to-square-feet" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Square Meters to Square Feet Converter</h3>
                <p className="text-gray-600">Convert square meters to square feet and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/acres-to-hectares" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Acres to Hectares Converter</h3>
                <p className="text-gray-600">Convert acres to hectares and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/square-kilometers-to-square-miles" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Square Kilometers to Square Miles Converter</h3>
                <p className="text-gray-600">Convert square kilometers to square miles and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/hectares-to-acres" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Hectares to Acres Converter</h3>
                <p className="text-gray-600">Convert hectares to acres and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/square-feet-to-square-meters" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Square Feet to Square Meters Converter</h3>
                <p className="text-gray-600">Convert square feet to square meters and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/square-miles-to-square-kilometers" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Square Miles to Square Kilometers Converter</h3>
                <p className="text-gray-600">Convert square miles to square kilometers and vice versa</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Speed Conversions <span className="emoji ml-2">🚀</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/km-h-to-mph" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Kilometers per Hour to Miles per Hour Converter</h3>
                <p className="text-gray-600">Convert km/h to mph and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/m-s-to-km-h" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Meters per Second to Kilometers per Hour Converter</h3>
                <p className="text-gray-600">Convert m/s to km/h and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/mph-to-km-h" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Miles per Hour to Kilometers per Hour Converter</h3>
                <p className="text-gray-600">Convert mph to km/h and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/knots-to-km-h" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Knots to Kilometers per Hour Converter</h3>
                <p className="text-gray-600">Convert knots to km/h and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/m-s-to-mph" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Meters per Second to Miles per Hour Converter</h3>
                <p className="text-gray-600">Convert m/s to mph and vice versa</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/converter/speed-of-light-converter" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Speed of Light Converter</h3>
                <p className="text-gray-600">Convert various speed units to fractions of the speed of light</p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionCalculators;